import { UUID } from '@cmg/common';
import {
  Alert,
  Box,
  Button,
  LoadingButton,
  Section,
  SnackbarManager,
  Stack,
  Typography,
} from '@cmg/design-system';
import { FormattedTime } from '@cmg/i18n';
import { Form, FormikProvider, useFormik } from 'formik';
import React from 'react';

import OfferingNoteForm, {
  SubmitOfferingNotesSchema,
} from '../../components/offering-note-form/OfferingNoteForm';
import { OfferingNoteFormValues } from '../../components/offering-note-form/types';
import { OfferingNotes_OfferingNotePartsFragment } from '../../components/offering-notes-list/components/offering-note/graphql';
import OfferingNotesList from '../../components/offering-notes-list/OfferingNotesList';
import { ViewMode } from '../../constants';
import {
  useOfferingSidePanel_CreateOfferingNoteMutation,
  useOfferingSidePanel_DeleteOfferingNoteMutation,
  useOfferingSidePanel_UpdateOfferingNoteMutation,
} from '../../graphql';

type Props = {
  offeringId: UUID;
  notes: OfferingNotes_OfferingNotePartsFragment[];
};

const OfferingNotes: React.FC<Props> = ({ offeringId, notes }) => {
  const [viewMode, setViewMode] = React.useState<ViewMode>(ViewMode.View);
  const [selectedNote, setSelectedNote] =
    React.useState<OfferingNotes_OfferingNotePartsFragment | null>(null);

  const [createOfferingNote, { loading: loadingCreateNote }] =
    useOfferingSidePanel_CreateOfferingNoteMutation({
      onCompleted: () => {
        SnackbarManager.success('Successfully created offering note');
        handleChangeViewMode(ViewMode.View);
      },
      onError: error => {
        SnackbarManager.error(`Failed to create offering note: ${error.message}`);
      },
    });

  const [updateOfferingNote, { loading: loadingUpdateNote }] =
    useOfferingSidePanel_UpdateOfferingNoteMutation({
      onCompleted: () => {
        SnackbarManager.success('Successfully updated offering note');
        handleChangeViewMode(ViewMode.View);
      },
      onError: error => {
        SnackbarManager.error(`Failed to update offering note: ${error.message}`);
      },
    });

  const [deleteOfferingNote, { loading: loadingDeleteNote }] =
    useOfferingSidePanel_DeleteOfferingNoteMutation({
      onCompleted: () => {
        SnackbarManager.success('Successfully deleted offering note');
        handleChangeViewMode(ViewMode.View);
      },
      onError: error => {
        SnackbarManager.error(`Failed to delete offering note: ${error.message}`);
      },
    });

  const loading = loadingCreateNote || loadingUpdateNote || loadingDeleteNote;

  const formik = useFormik<OfferingNoteFormValues>({
    validationSchema: SubmitOfferingNotesSchema,
    initialValues: { subject: selectedNote?.subject ?? null, note: selectedNote?.note ?? '' },
    onSubmit: async values => {
      if (selectedNote?.id) {
        await updateOfferingNote({
          variables: {
            offeringId,
            input: { subject: values.subject!, note: values.note, id: selectedNote.id },
          },
        });
      } else {
        await createOfferingNote({
          variables: {
            offeringId,
            input: { subject: values.subject!, note: values.note },
          },
        });
      }
    },
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
  });

  const handleChangeViewMode = (newViewMode: ViewMode) => {
    setSelectedNote(null);
    setViewMode(newViewMode);
    formik.resetForm();
  };

  const handleEditOfferingNote = (noteId: string) => {
    const note = notes.find(n => n.id === noteId);
    setSelectedNote(note || null);
    setViewMode(ViewMode.Edit);
  };

  const handleDeleteOfferingNote = async (noteId: string) => {
    await deleteOfferingNote({ variables: { offeringId, input: { id: noteId } } });
  };

  const getTitle = (mode: ViewMode) => {
    switch (mode) {
      case ViewMode.View:
        return 'Notes';
      case ViewMode.Edit:
        return 'Edit Note';
      case ViewMode.Create:
        return 'Create New Note';
      default:
        return 'Notes';
    }
  };

  return (
    <Stack data-testid="notes-section">
      <Alert severity="info">The following information is visible to your firm only</Alert>
      <Section
        data-testid="notes-action-panel-section"
        title={getTitle(viewMode)}
        pt={1}
        actions={[
          ...(viewMode === ViewMode.View
            ? [
                <Button
                  key="create"
                  aria-label="Create Note"
                  variant="contained"
                  onClick={() => handleChangeViewMode(ViewMode.Create)}
                >
                  Create
                </Button>,
              ]
            : [
                <Button
                  key="cancel"
                  aria-label="Cancel Note"
                  variant="outlined"
                  onClick={() => handleChangeViewMode(ViewMode.View)}
                >
                  Cancel
                </Button>,
                <LoadingButton
                  form="offering-note-form"
                  type="submit"
                  aria-label="Save Note"
                  variant="contained"
                  loading={loading}
                  key="save"
                >
                  Save
                </LoadingButton>,
              ]),
        ]}
      />
      <FormikProvider value={formik}>
        <Form id="offering-note-form" noValidate data-testid="offering-note-form">
          {viewMode !== ViewMode.View && (
            <div>
              <OfferingNoteForm loading={loading} />
              {viewMode === ViewMode.Edit && selectedNote && (
                <Stack direction="row" mt={2} ml={1} data-testid="note-timestamp" gap={1}>
                  <Typography variant="body1" color="text.secondary">
                    Last Updated:
                  </Typography>
                  <Typography variant="body1">
                    {selectedNote.lastEditedBy}{' '}
                    <FormattedTime value={selectedNote.lastEditedAt} format="dateTime" />
                  </Typography>
                </Stack>
              )}
            </div>
          )}
        </Form>
      </FormikProvider>
      {viewMode === ViewMode.View && (
        <Box pr={2}>
          <OfferingNotesList
            notes={notes}
            handleEditOfferingNote={handleEditOfferingNote}
            handleDeleteOfferingNote={handleDeleteOfferingNote}
          />
        </Box>
      )}
    </Stack>
  );
};

export default OfferingNotes;
